import { CareTeamMember } from 'src/app/core/models/care-team-member';
import { Diagnosis } from 'src/app/core/models/diagnosis';
import { Employer } from 'src/app/core/models/employer';
import { PatientMedication } from 'src/app/core/models/patient-medication';
import { Provider } from 'src/app/core/models/provider';
import { ProviderGroup } from 'src/app/core/models/provider-group';
import { Specialist } from 'src/app/core/models/specialist';
import { HospitalUserDetails } from '../models/hospital-user-details';
import { MemberAdmissionFacility } from '../models/member-admission-facility';
import { PatientCareManager } from '../models/patient-care-manager';
import { PatientExternalAgencyUsers } from '../models/patient-external-agency-user';
import { UserForTag } from '../models/user';
import { Individual } from './../models/individual';
import { MemberSearch } from './../models/member-search';
import { isEmpty } from './object-util';
import { isString } from './type-util';

export function formatIndividualName(individual: Individual): string {
  if (individual?.firstName || individual?.lastName) {
    return [individual?.firstName, individual?.lastName].filter((name) => !isEmpty(name)).join(' ');
  }

  return '';
}

export function formatNameWithEmployer(individual: Individual): string {
  if (individual?.firstName || individual?.lastName) {
    return [individual?.firstName, individual?.lastName, individual?.employer ? `(${individual.employer})` : '']
      .filter((name) => !isEmpty(name))
      .join(' ');
  }

  return '';
}

export function formatMemberQuickSearch(memberSearch: MemberSearch | string): string {
  if (isString(memberSearch)) {
    return memberSearch;
  } else if (memberSearch) {
    return `${memberSearch.lastName}, ${memberSearch.firstName} (${memberSearch.displayId})`;
  }

  return '';
}

export function formatCareCoordinatorName(patientCareManager: PatientCareManager): string {
  if (patientCareManager?.firstName || patientCareManager?.lastName) {
    return [
      patientCareManager?.firstName,
      patientCareManager?.lastName,
      patientCareManager?.employer ? `(${patientCareManager.employer})` : ''
    ]
      .filter((name) => !isEmpty(name))
      .join(' ');
  }

  return '';
}

export function formatExternalAgencyUserName(patientExternalAgencyUsers: PatientExternalAgencyUsers): string {
  if (patientExternalAgencyUsers?.firstName || patientExternalAgencyUsers?.lastName) {
    return [
      patientExternalAgencyUsers?.firstName,
      patientExternalAgencyUsers?.lastName,
      patientExternalAgencyUsers?.employer ? `(${patientExternalAgencyUsers.employer})` : ''
    ]
      .filter((name) => !isEmpty(name))
      .join(' ');
  }

  return '';
}

export function formatHospitalUserUserName(patientHospitalUserUsers: HospitalUserDetails): string {
  if (patientHospitalUserUsers?.firstName || patientHospitalUserUsers?.lastName) {
    return [
      patientHospitalUserUsers?.firstName,
      patientHospitalUserUsers?.lastName,
      patientHospitalUserUsers?.employer ? `(${patientHospitalUserUsers.employer})` : ''
    ]
      .filter((name) => !isEmpty(name))
      .join(' ');
  }

  return '';
}

export function formatSpecialistName(specialist: Specialist): string {
  if (specialist?.firstname || specialist?.lastname) {
    return [specialist?.firstname, specialist?.lastname].filter((name) => !isEmpty(name)).join(' ');
  }

  return '';
}

export function formatProviderName(provider: Provider): string {
  let formattedValue = formatIndividualName(provider);

  if (provider?.providerId) {
    formattedValue += ` (${provider.providerId})`;
  }

  return formattedValue;
}

export function formatCareTeamMemberName(careTeamMember: CareTeamMember): string {
  if (careTeamMember?.firstname || careTeamMember?.lastname) {
    return [careTeamMember?.firstname, careTeamMember?.lastname, careTeamMember?.role ? `(${careTeamMember.role})` : '']
      .filter((name) => !isEmpty(name))
      .join(' ');
  }

  return '';
}

export function formatEmployer(employer: Employer): string {
  return employer ? `${employer.description} - ${employer.employergrpid}` : '';
}

export function formatMedication(medication: PatientMedication): string {
  return medication?.medicationdescription || '';
}

export function formatDiagnosis(diagnosis: Diagnosis): string {
  if (diagnosis?.shortdescription && diagnosis?.diagcode) {
    return `${diagnosis.shortdescription} (${diagnosis.diagcode})`;
  }

  return '';
}

export function formatUserForTag(user: UserForTag): string {
  return user?.value || '';
}

export function formatMedicalHome(medicalHome: ProviderGroup): string {
  return medicalHome?.description || '';
}

export function formatFacility(facility: MemberAdmissionFacility | string): string {
  return (isString(facility) ? facility : facility?.description) || '';
}
